/**
 * <OnboardingProgressWidget /> — pinned full-width on Mission
 * Control until the onboarding journey is 100% complete.
 *
 * Per the M27 spec lock #7: "Mission Control shows an 'Onboarding
 * progress' widget when < 100% complete." This is the
 * always-discoverable surface — the dedicated /onboarding page is
 * the canonical detail.
 *
 * Snooze:
 *   - "Hide for 7 days" sets dismissed_until = now + 7 days
 *   - The widget hides until that timestamp passes
 *   - dismissed_until is per-tenant (not per-user) for v1
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, EyeOff, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../../lib/api";
import {
  ONBOARDING_JOURNEY_QUERY_KEY,
  isComplete,
  isDismissed,
  nextActiveStep,
  percentComplete,
  type OnboardingState,
  type StepSlug,
} from "../../lib/onboarding";

const _SNOOZE_DAYS = 7;

const _STEP_LABEL_KEYS: Record<StepSlug, string> = {
  welcome: "app.onb_step_welcome",
  company: "app.onb_step_company",
  policies: "app.onb_step_policies",
  "jobs-grades": "app.onb_step_jobs_grades",
  employees: "app.onb_step_employees",
  approvals: "app.onb_step_approvals",
  integrations: "app.onb_step_integrations",
  "shadow-payroll": "app.onb_step_shadow_payroll",
  "go-live": "app.onb_step_go_live",
};

export function OnboardingProgressWidget() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
    // Refresh on every mount (so navigating back to Mission
    // Control after completing a step shows fresh stats without a
    // hard refresh) + on window focus (catch progress made in
    // another tab) + every 30s as a background safety net.
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const [closed, setClosed] = useState(false);

  const snooze = useMutation({
    mutationFn: () =>
      api.updateOnboardingJourneyState({
        dismissed_until: new Date(
          Date.now() + _SNOOZE_DAYS * 86_400_000,
        ).toISOString(),
      }),
    onSuccess: (next) => qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next),
  });

  if (q.isLoading || q.error || !q.data) return null;
  const state = q.data as OnboardingState;
  if (isComplete(state)) return null;
  if (isDismissed(state)) return null;
  if (closed) return null;

  const pct = percentComplete(state);
  const next = nextActiveStep(state);
  const done = Math.round((pct / 100) * 9);

  return (
    <section
      role="region"
      aria-label={t("app.onb_widget_aria")}
      className="relative overflow-hidden rounded-xl border border-ai-subtle bg-ai-bg p-4"
    >
      {/* 2px gradient strip — signature signal that this is an
          important guided surface, not chrome. */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
      />
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-default text-ai">
          <Sparkles size={16} aria-hidden />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold text-text-primary">
            {t("app.onb_widget_progress", { done })}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            {t("app.onb_widget_upnext")} <strong>{t(_STEP_LABEL_KEYS[next])}</strong>
          </p>
          <div
            className="mt-2 h-1.5 w-full rounded-full bg-surface-default"
            aria-hidden
          >
            <div
              className="h-full rounded-full bg-brand-gradient-h transition-[width] duration-small ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to={`/onboarding/${next}`}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            {t("app.onb_widget_continue")}
            <ArrowRight size={14} aria-hidden className="rtl:rotate-180" />
          </Link>
          <button
            type="button"
            onClick={() => snooze.mutate()}
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-2 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken"
            title={t("app.onb_widget_hide_for_7")}
          >
            <EyeOff size={12} aria-hidden /> {t("app.onb_widget_hide")}
          </button>
          <button
            type="button"
            aria-label={t("app.onb_widget_close")}
            onClick={() => setClosed(true)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-text-tertiary hover:bg-surface-sunken"
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      </div>
    </section>
  );
}
